import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { fetchProductIdOfferingIds } from '../../queries/sxp/fetch-product-offering-ids';
import { OfferingAndPricingPlan } from '../types';
import {
  FetchProductIdOfferingIdsQuery,
  FetchProductIdOfferingIdsQueryVariables,
  ProductOffering,
} from '@css-xp/graphtypes/src/generated-types-sxp';
import { getPricingPlanFilterOptionsForNativeCatalog } from './get-catalog-pricing-plan-filters';
import { formatPricingPlansForStore } from './format-pricing-plans';

const sxpGraphqlClient = abgGraphqlClient(ProxyGraphClients.SXP);

const getPricingPlanIds = async ({
  productId,
  isMultiCurrencyEnabled,
  isAddOnsSearchEnabled,
}: {
  productId: string;
  isMultiCurrencyEnabled: boolean;
  isAddOnsSearchEnabled?: boolean;
}) => {
  const filterOptions = getPricingPlanFilterOptionsForNativeCatalog(isMultiCurrencyEnabled);

  const { data, error } = await sxpGraphqlClient.query<
    FetchProductIdOfferingIdsQuery,
    FetchProductIdOfferingIdsQueryVariables
  >({
    query: fetchProductIdOfferingIds,
    variables: {
      id: productId,
      filterOptions,
      isAddOnsSearchEnabled,
    },
  });
  if (error) {
    return {
      [productId]: null,
    };
  }

  const offerings = data?.nativeCatalogItem?.offering;

  const productIdOfferings = offerings?.map((offering) => {
    const { defaultPricingPlan, USDPricingPlanEligibility, pricingPlans } = formatPricingPlansForStore({
      availablePricingPlans: offering?.availablePricingPlans,
    });

    return {
      offeringName: offering?.offeringName ?? '',
      id: offering?.id ?? '',
      level: 0,
      pricingType: offering?.pricingType ?? '',
      defaultPricingPlan,
      defaultPricingPlansEligibilityMap: USDPricingPlanEligibility,
      pricingPlans,
      availablePricingPlans: pricingPlans,
      relationshipRequirements: offering?.relationshipRequirements ?? [],
      offeringGroup: offering?.offeringGroup,
    };
  });

  return {
    [productId]: productIdOfferings,
  };
};

export const getProductIdsOfferingId = async ({
  productIds,
  existingOfferingsAndPricingPlans = {},
  isMultiCurrencyEnabled = false,
  isAddOnsSearchEnabled = false,
}: {
  productIds: string[];
  existingOfferingsAndPricingPlans?: Record<string, OfferingAndPricingPlan[]>;
  isMultiCurrencyEnabled?: boolean;
  isAddOnsSearchEnabled?: boolean;
}) => {
  const productIdsToBeFetched = productIds.filter((productId) => !existingOfferingsAndPricingPlans[productId]);

  const promises = productIdsToBeFetched.map((productId) => {
    return getPricingPlanIds({ productId, isMultiCurrencyEnabled, isAddOnsSearchEnabled });
  });
  const productIdsOfferingArr = await Promise.all(promises);

  productIdsOfferingArr.forEach((productIdOfferings, index) => {
    const productId = productIdsToBeFetched[index];
    if (productIdOfferings[productId] && productId) {
      existingOfferingsAndPricingPlans[productId] = productIdOfferings[productId] as OfferingAndPricingPlan[];
    }
  });

  return existingOfferingsAndPricingPlans;
};
